import React from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  useTheme,
  IconButton,
} from "@mui/material";
import { Circle } from "@mui/icons-material";

import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import { PilotProps } from "../types/view-pilot";

import {
  DocumentIcon,
  DownloadIcon,
  EnvelopeIcon,
  ExportIcon,
  LocationIcon,
  PhoneIcon,
  RatingIcon,
  SkillsIcon,
  TrashIcon,
} from "../../../components/icons";

import { samplePDFDownloads } from "../../../data/downloads";

export interface UserDetails {
  id: number;
  user_infor: {
    browser?: string | null;
    operating_system?: string | null;
    device_type?: string | null;
    has_pixel_pilot_device?: boolean | null;
    device_information?: {
      platform?: string | null;
      manufacturer?: string | null;
      device_model?: string | null;
      os_version?: string | null;
      app_version?: string | null;
    } | null;
    location: string | null;
  };
}

interface PilotOverviewProps {
  pilot: PilotProps;
  userDetails: UserDetails | null;
}

export const PilotOverview: React.FC<PilotOverviewProps> = ({ pilot ,userDetails }) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const pdfDownloads = (pdfSource: string, pdfTitle: string) => {
    const downlink = document.createElement("a");
    downlink.href = pdfSource;
    downlink.download = `${pdfTitle}.pdf`;

    downlink.click();
  };

  const viewPdf = (pdfSource: string) => {
    if (typeof window !== "undefined") {
      window.open(pdfSource, "_blank");
    } else {
      console.log("Window object is not defined.");
    }
  };

  if (!pilot) return null;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                pb: "8px",
                borderBottom: 1,
                borderColor: "divider",
                mb: "8px",
              }}
              gap={"4px"}
            >
              <SkillsIcon color="disabled" sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Contact Information
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <EnvelopeIcon color="disabled" sx={{ fontSize: 18 }} />
              <Typography variant="body2">{pilot?.email}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <PhoneIcon color="disabled" sx={{ fontSize: 18 }} />
              <Typography variant="body2">{pilot?.phone}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
            <LocationIcon color="disabled" sx={{ fontSize: 18 }} />

            {userDetails?.user_infor?.location ? (
              <Typography variant="body2">
                {userDetails.user_infor.location}
              </Typography>
            ) : (
              <Chip
                label={<i>Location Not Provided</i>}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  borderColor: colors.primary[800],
                }}
              />
            )}
          </Box>

            <Box sx={{ padding: 1, borderRadius: 1, bgcolor: backgroundColor }}>
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                mb={1}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Emergency Contact
                </Typography>
              </Box>
              <Typography variant="subtitle2">
                {pilot.emergencyContactName || "Unnamed Emergency Contact"}
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIcon color="disabled" sx={{ fontSize: 18 }} />
                <Typography variant="body2">
                  {pilot.emergencyContact}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                pb: "8px",
                borderBottom: 1,
                borderColor: "divider",
                mb: "8px",
              }}
              gap={"4px"}
            >
              <DocumentIcon color="disabled" sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Documents
              </Typography>
            </Box>
            {samplePDFDownloads?.map((dl) => {
              return (
                <Box
                  display={"flex"}
                  key={dl.id}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  sx={{
                    padding: 1,
                    mb: 1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: backgroundColor,
                  }}
                >
                  <Box display={"flex"} alignItems={"center"} gap={1}>
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        padding: 1,
                        bgcolor: backgroundColor,
                      }}
                    >
                      <DocumentIcon color="disabled" sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant={"body2"} fontWeight={600}>
                        {dl.title}
                      </Typography>
                      <Box display={"flex"} alignItems={"center"} gap={1}>
                        <Typography variant={"body2"} fontWeight={"semi-bold"}>
                          2.3 MB
                        </Typography>
                        <Circle sx={{ width: "4px" }} />
                        <Typography variant={"body2"} fontWeight={"semi-bold"}>
                          {dl.dateUploaded}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box display={"flex"} alignItems={"center"} gap={1}>
                    <IconButton onClick={() => viewPdf(dl.src)}>
                      <ExportIcon color="disabled" sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton onClick={() => pdfDownloads(dl.src, dl.title)}>
                      <DownloadIcon color="info" sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                      onClick={() => console.log("deleting pdf file")}
                    >
                      <TrashIcon color="error" sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                pb: "8px",
                borderBottom: 1,
                borderColor: "divider",
                mb: "8px",
              }}
              gap={"4px"}
            >
              <SkillsIcon color="disabled" sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Skills & Equipment
              </Typography>
            </Box>

            <Typography
              variant="subtitle2"
              mb={1}
              sx={{ color: colors.primary[100] }}
            >
              Skills
            </Typography>
            {pilot.skills?.length === 0 ? (
              <Chip
                label={<i>No Skills Specified</i>}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  mb: 2,
                  borderColor: colors.primary[800],
                }}
              />
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {pilot.skillsDetail.map((skill) => (
                  <Chip
                    key={skill.id}
                    label={skill.name}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 1,
                      borderColor: colors.primary[800],
                    }}
                  />
                ))}
              </Box>
            )}

            <Typography
              variant="subtitle2"
              mb={1}
              sx={{ color: colors.primary[100] }}
            >
              Equipment
            </Typography>
            {pilot.tools?.length === 0 ? (
              <Chip
                label={<i>No Tools Specified</i>}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  borderColor: colors.primary[800],
                }}
              />
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {pilot.toolsDetail.map((tool) => (
                  <Chip
                    key={tool.id}
                    label={tool.name}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 1,
                      borderColor: colors.primary[800],
                    }}
                  />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                pb: "8px",
                borderBottom: 1,
                borderColor: "divider",
                mb: "8px",
              }}
              gap={"4px"}
            >
              <RatingIcon color="disabled" sx={{ fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Certificates
              </Typography>
            </Box>
            <Typography variant={"h6"}>{"Pilot Certificates Here"}</Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default PilotOverview;
